/**
 * Vista de Simuladores Deterministas de FinVoz (FR-SIM-1..6, spec/01-requirements.md §10).
 *
 * Principios:
 *  - Determinismo matematico y reproducible (FR-SIM-4)
 *  - Funciona 100% offline (FR-SIM-6)
 *  - Descargo visible de no asesoramiento financiero (FR-SIM-5)
 *  - Calculos puros sin alterar el libro contable
 */

import type { AppState } from "../types.js";
import { formatMinorUnits } from "../format.js";
import {
  simulateCompoundInterest,
  amortizationSchedule,
  periodsToSavingsGoal,
} from "../../simulators/simulators.js";

export function renderSimulators(state?: AppState): string {
  const currency = state?.currency || "PEN";
  const scale = 2;
  const curSymbol = currency === "PEN" ? "S/" : currency === "USD" ? "$" : "€";

  // Calculos iniciales por defecto para mostrar valores inmediatos
  // 1. Interes compuesto: 10.000€ inicial, 200€/mes, 7% anual (~5833 micros/mes), 10 años (120 meses)
  const defaultCompound = simulateCompoundInterest(1000000n, 20000n, 5833n, 120);

  // 2. Amortizacion prestamo: 150.000€, 3.5% anual (~2916 micros/mes), 25 años (300 meses)
  const defaultSchedule = amortizationSchedule(15000000n, 2916n, 300);
  const defaultCuota = defaultSchedule[0]?.payment ?? 0n;
  const totalPagado = defaultSchedule.reduce((sum, r) => sum + r.payment, 0n);
  const totalIntereses = defaultSchedule.reduce((sum, r) => sum + r.interest, 0n);

  // 3. Meta de ahorro: 5.000€ actual, 300€/mes, 20.000€ meta, 2% anual (~166 micros/mes)
  const defaultMonthsToGoal = periodsToSavingsGoal(500000n, 30000n, 166n, 2000000n);

  return `
    <div class="simulators-view" data-testid="simulators-view">
      <!-- Descargo obligatorio (FR-SIM-5) -->
      <div style="background-color: var(--bg-tertiary); border-left: 4px solid var(--warning); padding: 12px 16px; border-radius: 6px; margin-bottom: 20px; font-size: 0.85rem;">
        <strong>Aviso legal y supuestos de simulación (FR-SIM-5):</strong> Las proyecciones mostradas son cálculos matemáticos deterministas y no constituyen asesoramiento financiero ni garantía de rentabilidad futura. Funcionan completamente sin conexión (FR-SIM-6).
      </div>

      <!-- Selector de Simulador -->
      <div style="display: flex; gap: 8px; margin-bottom: 16px; overflow-x: auto;">
        <button id="sim-tab-btn-ci" class="btn-primary" data-testid="sim-tab-compound" data-action="switch-sim-tab" data-sim-tab="ci">
          📈 Interés Compuesto
        </button>
        <button id="sim-tab-btn-loan" class="btn-secondary" data-testid="sim-tab-loan" data-action="switch-sim-tab" data-sim-tab="loan">
          🏠 Amortización de Préstamo
        </button>
        <button id="sim-tab-btn-goal" class="btn-secondary" data-testid="sim-tab-goal" data-action="switch-sim-tab" data-sim-tab="goal">
          🎯 Tiempo hasta Meta
        </button>
      </div>

      <!-- SIMULADOR 1: INTERES COMPUESTO (FR-SIM-1) -->
      <div id="sim-panel-ci" class="card" data-testid="sim-panel-compound">
        <div class="card-header">
          <h2 class="card-title">Simulador de Interés Compuesto</h2>
          <span class="badge badge-manual">Determinista (FR-SIM-1)</span>
        </div>

        <div class="grid-2" style="margin-bottom: 20px;">
          <!-- Formulario de Entrada -->
          <div>
            <div style="margin-bottom: 12px;">
              <label for="ci-principal" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Capital Inicial (${curSymbol}):</label>
              <input type="number" id="ci-principal" data-testid="ci-principal" data-sim-input="compound" value="10000" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="ci-contribution" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Aportación Mensual (${curSymbol}):</label>
              <input type="number" id="ci-contribution" data-testid="ci-contribution" data-sim-input="compound" value="200" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="ci-rate" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Rentabilidad Anual Estimada (%):</label>
              <input type="number" step="0.1" id="ci-rate" data-testid="ci-rate" data-sim-input="compound" value="7.0" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="ci-years" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Horizonte Temporal (Años):</label>
              <input type="number" min="1" max="50" id="ci-years" data-testid="ci-years" data-sim-input="compound" value="10" style="width: 100%;" />
            </div>
          </div>

          <!-- Resultados -->
          <div style="background-color: var(--bg-tertiary); padding: 20px; border-radius: 8px; display: flex; flex-direction: column; justify-content: space-around;">
            <div>
              <span class="metric-label">Capital Final Proyectado</span>
              <div id="ci-res-final" class="metric-value metric-positive" data-testid="ci-res-final">
                ${formatMinorUnits(defaultCompound.finalBalance, currency, scale)}
              </div>
            </div>
            <div style="border-top: 1px solid var(--border); padding-top: 12px; margin-top: 12px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.9rem; margin-bottom: 6px;">
                <span style="color: var(--text-secondary);">Total Aportado:</span>
                <span id="ci-res-contributed" style="font-family: var(--font-mono); font-weight: 600;">
                  ${formatMinorUnits(1000000n + defaultCompound.totalContributed, currency, scale)}
                </span>
              </div>
              <div style="display: flex; justify-content: space-between; font-size: 0.9rem;">
                <span style="color: var(--text-secondary);">Intereses Generados:</span>
                <span id="ci-res-interest" style="font-family: var(--font-mono); font-weight: 600; color: var(--success);">
                  ${formatMinorUnits(defaultCompound.totalInterest, currency, scale)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SIMULADOR 2: AMORTIZACION DE PRESTAMO (FR-SIM-2) -->
      <div id="sim-panel-loan" class="card" style="display: none;" data-testid="sim-panel-loan">
        <div class="card-header">
          <h2 class="card-title">Simulador de Amortización de Préstamo</h2>
          <span class="badge badge-manual">Sistema Francés (FR-SIM-2)</span>
        </div>

        <div class="grid-2" style="margin-bottom: 20px;">
          <div>
            <div style="margin-bottom: 12px;">
              <label for="loan-amount" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Importe del Préstamo (${curSymbol}):</label>
              <input type="number" id="loan-amount" data-testid="loan-amount" data-sim-input="loan" value="150000" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="loan-rate" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Tipo de Interés Anual (%):</label>
              <input type="number" step="0.05" id="loan-rate" data-testid="loan-rate" data-sim-input="loan" value="3.5" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="loan-years" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Plazo (Años):</label>
              <input type="number" min="1" max="40" id="loan-years" data-testid="loan-years" data-sim-input="loan" value="25" style="width: 100%;" />
            </div>
          </div>

          <div style="background-color: var(--bg-tertiary); padding: 20px; border-radius: 8px; display: flex; flex-direction: column; justify-content: space-around;">
            <div>
              <span class="metric-label">Cuota Mensual Estimada</span>
              <div id="loan-res-payment" class="metric-value" data-testid="loan-res-payment">
                ${formatMinorUnits(defaultCuota, currency, scale)} / mes
              </div>
            </div>
            <div style="border-top: 1px solid var(--border); padding-top: 12px; margin-top: 12px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.9rem; margin-bottom: 6px;">
                <span style="color: var(--text-secondary);">Total a Pagar:</span>
                <span id="loan-res-total" style="font-family: var(--font-mono); font-weight: 600;">
                  ${formatMinorUnits(totalPagado, currency, scale)}
                </span>
              </div>
              <div style="display: flex; justify-content: space-between; font-size: 0.9rem;">
                <span style="color: var(--text-secondary);">Total Intereses:</span>
                <span id="loan-res-interest" style="font-family: var(--font-mono); font-weight: 600; color: var(--warning);">
                  ${formatMinorUnits(totalIntereses, currency, scale)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SIMULADOR 3: TIEMPO HASTA META (FR-SIM-3) -->
      <div id="sim-panel-goal" class="card" style="display: none;" data-testid="sim-panel-goal">
        <div class="card-header">
          <h2 class="card-title">Simulador de Tiempo hasta Meta</h2>
          <span class="badge badge-manual">Determinista (FR-SIM-3)</span>
        </div>

        <div class="grid-2">
          <div>
            <div style="margin-bottom: 12px;">
              <label for="goal-start" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Ahorro Actual (${curSymbol}):</label>
              <input type="number" id="goal-start" data-testid="goal-start" data-sim-input="goal" value="5000" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="goal-target-val" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Importe Objetivo (${curSymbol}):</label>
              <input type="number" id="goal-target-val" data-testid="goal-target-val" data-sim-input="goal" value="20000" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="goal-monthly" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Aportación Mensual (${curSymbol}):</label>
              <input type="number" id="goal-monthly" data-testid="goal-monthly" data-sim-input="goal" value="300" style="width: 100%;" />
            </div>
          </div>

          <div style="background-color: var(--bg-tertiary); padding: 20px; border-radius: 8px; display: flex; flex-direction: column; justify-content: center;">
            <span class="metric-label">Tiempo Estimado</span>
            <div id="goal-res-time" class="metric-value metric-positive" data-testid="goal-res-time">
              ${defaultMonthsToGoal !== null ? `${defaultMonthsToGoal} meses (~${(defaultMonthsToGoal / 12).toFixed(1)} años)` : "Inalcanzable"}
            </div>
            <div style="font-size: 0.85rem; color: var(--text-secondary); margin-top: 8px;">
              Con aportación cero el resultado es inalcanzable, nunca infinito ni error (FR-SIM-3).
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}
